import React, { useState, useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import Icon from './Icon';
import '../css/premium-select.css';

/**
 * Полупрозрачный «стеклянный» выпадающий список.
 * Общий компонент для админки (создание сотрудника) и экрана логина.
 *
 * options: [{ value, label, icon?, description? }]
 * placeholder / placeholderIcon — вид, когда ничего не выбрано (value пустой).
 */
const PremiumSelect = ({
  value,
  onChange,
  options = [],
  placeholder,
  placeholderIcon = 'shield',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const selectedOption = options.find((opt) => opt.value === value);
  const hasPlaceholder = !selectedOption && placeholder != null;
  const triggerLabel = selectedOption ? selectedOption.label : (placeholder || options[0]?.label);
  const triggerIcon = selectedOption ? (selectedOption.icon || 'shield') : placeholderIcon;

  return (
    <div className="premium-select-container" ref={containerRef}>
      <button
        type="button"
        className={`premium-select-trigger ${isOpen ? 'open' : ''} ${hasPlaceholder ? 'is-placeholder' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="trigger-content">
          <Icon name={triggerIcon} size={16} className="trigger-icon" />
          <span>{triggerLabel}</span>
        </div>
        <Icon name="chevron-down" size={14} className={`arrow-icon ${isOpen ? 'rotate' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="premium-select-dropdown"
            initial={{ opacity: 0, y: 8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.95 }}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
          >
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`premium-select-option ${value === option.value ? 'active' : ''}`}
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
              >
                <Icon name={option.icon || 'shield'} size={14} className="option-icon" />
                <div className="option-text">
                  <span className="option-label">{option.label}</span>
                  {option.description && <span className="option-desc">{option.description}</span>}
                </div>
                {value === option.value && <Icon name="check" size={14} className="check-icon" />}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PremiumSelect;
